import React from 'react';
import {
  formatDate,
} from 'utils/date';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import Name from 'pages/Dashboard/components/Name';
import EOBActions from 'pages/Dashboard/pages/Billing/Payments/EOBActions';
import AmountTag, {
  defaultNegativeClass,
} from 'pages/Dashboard/pages/Billing/AmountTag';
import AutoIcon from '@mui/icons-material/MotionPhotosAuto';
import CopyWrapper from 'core/components/CopyWrapper';
import isNil from 'lodash/isNil';
import {
  EOBSummaryResponseDTO,
} from 'dtos/eOBSummaryResponseDTO';
import UnpostedAmountTag from 'pages/Dashboard/pages/Billing/Payments/columns/UnpostedAmountTag';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import EOBPaymentNumber from 'pages/Dashboard/pages/Billing/Payments/columns/EOBPaymentNumber';
import {
  Row,
} from '@tanstack/react-table';
import {
  useUserProfilePicturePath,
} from 'pages/Dashboard/hooks/media';

export const eobPaymentAmountClasses = {
  positive: 'bg-transparent !text-gray-500 !border-0',
  negative: defaultNegativeClass,
  neutral: defaultNegativeClass,
};

type EOBRowProps = {
  row: Row<EOBSummaryResponseDTO>;
};

function CreatedCell({
  row: { original: { tag, createdOn, createdByUserName, createdByUserId } },
}: EOBRowProps) {
  const isManual = /manual/i.test(tag ?? 'Manual');
  const src = useUserProfilePicturePath(createdByUserId ?? undefined);

  return (
    <div className="flex flex-col gap-1 w-full overflow-x-hidden">
      <strong className="text-gray-500 text-xs font-semibold">
        {formatDate(createdOn ?? '')}
      </strong>
      <Name
        name={isManual
          ? getDisplayName(createdByUserName)
          : 'Auto'}
        {...(isManual ? createdByUserName : {})}
        image={isManual
          ? { size: 'xs', src }
          : <AutoIcon className="!w-5 !h-5 !text-gray-600" />}
      />
    </div>
  );
}

function ReviewedCell({
  row: { original: { updatedOn, reviewedByUserName, reviewedByUserId } },
}: EOBRowProps) {
  const src = useUserProfilePicturePath(reviewedByUserId ?? undefined);

  return isNil(reviewedByUserName)
    ? DEFAULT_EMPTY_VALUE
    : (
      <div className="flex flex-col gap-1 overflow-x-hidden w-full">
        <strong className="text-gray-500 text-xs font-semibold">
          {formatDate(updatedOn ?? '')}
        </strong>
        <Name {...reviewedByUserName} image={{ size: 'xs', src }} />
      </div>
    );
}

const columns: ColumnDef<EOBSummaryResponseDTO>[] = [{
  id: 'eobNumber',
  header: 'EOB #',
  size: 220,
  cell: ({ row: { original: { eobNumber } } }) => (
    <CopyWrapper
      text={eobNumber ?? ''}
      tooltip="Copy EOB Number"
    >
      <strong className="max-w-full truncate text-gray-500">
        {eobNumber}
      </strong>
    </CopyWrapper>
  ),
}, {
  id: 'payer',
  header: 'Payer',
  meta: { autoSized: true },
  minSize: 180,
  cell: ({ row: { original: { insuranceName } } }) => (
    <CopyWrapper
      text={insuranceName ?? ''}
      tooltip="Copy Payer Name"
    >
      <div className="font-semibold">
        {insuranceName ?? '--'}
      </div>
    </CopyWrapper>
  ),
}, {
  id: 'paymentMethod',
  header: 'Payment Reference',
  minSize: 150,
  maxSize: 250,
  meta: { autoSized: true },
  cell: ({ row: { original: {
    insurancePaymentType,
    transactionIdentifier,
    paymentDate,
  } } }) => (
    <EOBPaymentNumber
      paymentType={insurancePaymentType ?? ''}
      transactionIdentifier={transactionIdentifier ?? undefined}
      paymentDate={paymentDate}
    />
  ),
}, {
  id: 'insurancePayment',
  header: 'Payment',
  multiLineHeader: true,
  size: 110,
  cell: ({ row: { original: { insurancePaymentAmount } } }) => (
    <AmountTag
      value={insurancePaymentAmount}
      classes={eobPaymentAmountClasses}
      useColors
    >
      {insurancePaymentAmount === 0 ? (<span className="text-gray-400">—</span>) : null}
    </AmountTag>
  ),
}, {
  id: 'unallocated',
  header: 'Unposted',
  accessorKey: 'eob.eobServiceCodePaymentInfos',
  size: 110,
  cell: ({ row: { original: { unpostedPaymentAmount } } }) => (
    <UnpostedAmountTag unallocated={unpostedPaymentAmount ?? 0} />
  ),
}, {
  id: 'createdBy',
  size: 180,
  header: 'Created',
  cell: ({ row }) => <CreatedCell row={row} />,
}, {
  id: 'reviewedByUser',
  size: 180,
  header: 'Reviewed',
  cell: ({ row }) => <ReviewedCell row={row} />,
}, {
  id: 'actions',
  header: '',
  size: 40,
  cell: ({ row }) => (
    <EOBActions {...row.original} />
  ),
}];

export default columns;
